import React, { useCallback, useEffect, useState } from "react";
import {
  IAgentChatTurn,
  IChatWithAI,
  IHistoryItem,
  IMessage,
  ISessionDetail,
  IUploadedFile,
} from "../../models/common/IChatBot";
import { connect } from "react-redux";
import {
  AgentType,
  AiChatRoles,
  AiClient,
  DEFAULT_OLLAMA_MODEL,
} from "../../constants/commonConstants";
import { apiConfig } from "../../constants/apiConfig";
import {
  aiChatAgent,
  submitFeedback,
  uploadFile,
  removeFile,
} from "../../action/common/chatBotActionCreator";
import { ISSECallbacks } from "../../helper/sseClient";
import {
  getPreviousChatMessage,
  updateAISessions,
  loadChatSessionMessages,
} from "../../action/ai/aiConfigurationActionCreator";
import { useStreamSession, SessionStatus } from "../../hooks/useStreamSession";
import BuduGPTChat from "./buduGPTChat";
import CopilotChat from "./copilotChat";
import { limit } from "../../constants/pageBlocks";

const SearchWithAI = (props: IChatWithAI) => {
  const {
    sendChatBotMessages,
    response,
    previousChatMessage,
    updateAIChatSessions,
    refreshHistory,
    loadSessionMessages,
    submitFeedbackAction,
    uploadFileAction,
    removeFileAction,
    agentKey,
    agentKeyLoading,
    currentUser,
  } = props;

  const [searchValue, setSearchValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [chatHistory, setChatHistory] = useState<IAgentChatTurn[]>([]);
  const [loader, setLoader] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(AgentType[0].value);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [trigger, setTrigger] = useState(0);
  const [copied, setCopied] = useState<boolean>(false);

  // BUDU-specific state
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [chatUserId, setChatUserId] = useState<string | null>(null);
  const [feedbackGiven, setFeedbackGiven] = useState<Record<number, number>>(
    {},
  );
  const [uploadedFile, setUploadedFile] = useState<IUploadedFile | null>(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [searchGlobally, setSearchGlobally] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [isFetchingSession, setIsFetchingSession] = useState<boolean>(false);

  const {
    isStreaming,
    isStreamingRef,
    setIsStreamingWithRef,
    sessionStatus,
    setSessionStatus,
    generationId,
    setGenerationId,
    isReconnectingRef,
    activeReconnectionsRef,
    abortRef,
    resetStream,
  } = useStreamSession();

  const isBudu = apiConfig.aiClient === AiClient.BUDU;
  const isBusy = loader || isStreaming || (isBudu && !!agentKeyLoading);

  const cleanResponse = (response: string) => {
    if (typeof response !== 'string') return '';
    return response
      .replace(/\s*\[\d+\]\s*/g, "\n")
      .replace(/\n*\[\d+\]:\s*cite:\d+\s*"[^"]*"\s*/g, "\n")
      .replace(/:\s*cite:\d+\s*"[^"]*"\s*/g, "\n")
      .replace(/\n{3,}/g, "\n")
      .replace(/\s{2,}/g, "\n")
      .trim();
  };

  useEffect(() => {
    if (isBudu || !response) return;

    setLoader(false);
    let i = 0;
    let botMessage = "";

    const typeWriter = () => {
      if (i < response.length) {
        botMessage += cleanResponse(response.charAt(i));
        setChatHistory((prev) => {
          const newHistory = [...prev];
          newHistory[newHistory.length - 1] = {
            role: AiChatRoles.ASSISTANT,
            content: botMessage,
          };
          return newHistory;
        });
        i++;
        setTimeout(typeWriter, 30);
      }
    };

    setChatHistory((prev) => [
      ...prev,
      { role: AiChatRoles.ASSISTANT, content: "" },
    ]);
    typeWriter();
  }, [trigger]);

  useEffect(() => {
    return () => resetStream();
  }, []);

  useEffect(() => {
    resetStream();
    setChatHistory([]);
    setFeedbackGiven({});
    setSessionId(null);
    setChatUserId(null);
    setUploadedFile(null);
    setUploadError(null);
    setSearchGlobally(false);
    setEditingIndex(null);
    setEditDraft("");
  }, [selectedAgent]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleStopGeneration = useCallback(() => {
    resetStream();
    setLoader(false);
    setChatHistory((prev) => {
      if (prev.length === 0) return prev;
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      const last = updated[lastIndex];
      if (last && last.role === AiChatRoles.ASSISTANT) {
        if (last.content === "") {
          setSessionStatus(undefined);
          return updated.slice(0, lastIndex);
        }
        setSessionStatus("Stopped");
      }
      return updated;
    });
  }, [resetStream, setSessionStatus]);

  const reconnectStream = useCallback(
    (genId: string, sessId: number) => {
      if (
        !agentKey ||
        isStreamingRef.current ||
        isReconnectingRef.current ||
        activeReconnectionsRef.current.has(genId)
      ) {
        return;
      }

      activeReconnectionsRef.current.add(genId);
      isReconnectingRef.current = true;
      setIsStreamingWithRef(true);
      setSessionStatus("Pending");
      setLoader(true);

      const controller = new AbortController();
      abortRef.current = controller;

      let hadError = false;

      const payload: IMessage = {
        message: "",
        toolType: selectedAgent,
        model: DEFAULT_OLLAMA_MODEL,
        sessionId: sessId,
        generationId: genId,
        userId: chatUserId,
        source: "BUDUPORTAL",
      };

      const callbacks: ISSECallbacks = {
        onChunk: (chunk) => {
          if (controller.signal.aborted) return;
          setLoader(false);
          setChatHistory((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last && last.role === AiChatRoles.ASSISTANT) {
              updated[updated.length - 1] = {
                ...last,
                content: last.content + chunk,
              };
            } else {
              updated.push({ role: AiChatRoles.ASSISTANT, content: chunk });
            }
            return updated;
          });
        },
        onError: (error) => {
          hadError = true;
          if (controller.signal.aborted) return;
          activeReconnectionsRef.current.delete(genId);
          setSessionStatus("Failed");
          setIsStreamingWithRef(false);
          isReconnectingRef.current = false;
          setLoader(false);
          setChatHistory((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last && last.role === AiChatRoles.ASSISTANT) {
              updated[updated.length - 1] = { ...last, content: error };
            }
            return updated;
          });
        },
        onSessionId: (_id, returnedGenId, cachedResponse) => {
          if (controller.signal.aborted) return;
          if (returnedGenId) setGenerationId(returnedGenId);
          if (cachedResponse !== undefined) {
            setChatHistory((prev) => {
              const updated = [...prev];
              const last = updated[updated.length - 1];
              if (last && last.role === AiChatRoles.ASSISTANT) {
                updated[updated.length - 1] = {
                  ...last,
                  content: cachedResponse,
                };
              } else {
                updated.push({
                  role: AiChatRoles.ASSISTANT,
                  content: cachedResponse,
                });
              }
              return updated;
            });
          }
        },
        onDone: () => {
          if (controller.signal.aborted || hadError) return;
          activeReconnectionsRef.current.delete(genId);
          setSessionStatus("Success");
          setIsStreamingWithRef(false);
          isReconnectingRef.current = false;
          setLoader(false);
        },
      };

      sendChatBotMessages(payload, agentKey, callbacks, controller.signal);
    },
    [
      agentKey,
      selectedAgent,
      chatUserId,
      sendChatBotMessages,
      setIsStreamingWithRef,
      setSessionStatus,
      setGenerationId,
      isStreamingRef,
      isReconnectingRef,
      activeReconnectionsRef,
      abortRef,
    ],
  );

  const handleButtonClick = useCallback(
    (suggestion?: string, overrideSessionId?: number, editIndex?: number) => {
      if (isStreamingRef.current || isReconnectingRef.current) return;
      if (isBudu && agentKeyLoading) return;

      const messageToSend =
        suggestion || searchValue.trim() || chatMessage.trim();
      if (!messageToSend) return;

      const baseHistory =
        editIndex !== undefined ? chatHistory.slice(0, editIndex) : chatHistory;

      setChatHistory((prev) => {
        const updated = editIndex !== undefined ? prev.slice(0, editIndex) : [...prev];
        return [...updated, { role: AiChatRoles.USER, content: messageToSend }];
      });

      if (editIndex !== undefined) {
        setFeedbackGiven((prev) => {
          const updated: Record<number, number> = {};
          Object.entries(prev).forEach(([key, value]) => {
            const idx = Number(key);
            if (idx < editIndex) updated[idx] = value;
          });
          return updated;
        });
      }

      const currentUserId = chatUserId || currentUser?.id || null;
      if (!chatUserId && currentUser?.id) {
        setChatUserId(currentUser.id);
      }

      const currentSessionId =
        overrideSessionId !== undefined ? overrideSessionId : sessionId;

      const chatInput: IMessage = {
        message: messageToSend,
        model: DEFAULT_OLLAMA_MODEL,
        toolType: selectedAgent,
        history: baseHistory,
        sessionId: currentSessionId,
        uploadId: uploadedFile?.uploadId ?? null,
        uploadFilename: uploadedFile?.filename ?? null,
        searchGlobally: uploadedFile ? searchGlobally : undefined,
        userId: currentUserId,
        source: "BUDUPORTAL",
      };

      setSearchValue("");
      setChatMessage("");
      setIsModalOpen(true);

      if (isBudu) {
        setIsStreamingWithRef(true);
        setSessionStatus("Pending");
        setLoader(true);

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setChatHistory((prev) => [
          ...prev,
          { role: AiChatRoles.ASSISTANT, content: "" },
        ]);

        let hadError = false;

        const callbacks: ISSECallbacks = {
          onSessionId: (id, returnedGenId, cachedResponse) => {
            if (controller.signal.aborted) return;
            setSessionId(id);
            if (returnedGenId) setGenerationId(returnedGenId);
            if (cachedResponse !== undefined) {
              setChatHistory((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === AiChatRoles.ASSISTANT) {
                  updated[updated.length - 1] = {
                    ...last,
                    content: cachedResponse,
                  };
                }
                return updated;
              });
            }
          },
          onChunk: (chunk) => {
            if (controller.signal.aborted) return;
            setLoader(false);
            setChatHistory((prev) => {
              const updated = [...prev];
              const last = updated[updated.length - 1];
              updated[updated.length - 1] = {
                ...last,
                content: last.content + chunk,
              };
              return updated;
            });
          },
          onError: (error) => {
            hadError = true;
            if (controller.signal.aborted) return;
            setSessionStatus("Failed");
            setIsStreamingWithRef(false);
            setLoader(false);
            setChatHistory((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: AiChatRoles.ASSISTANT,
                content: error,
              };
              return updated;
            });
          },
          onDone: () => {
            if (controller.signal.aborted || hadError) return;
            setLoader(false);
            setIsStreamingWithRef(false);
            setSessionStatus("Success");
            refreshHistory(limit.five);
          },
        };

        sendChatBotMessages(chatInput, agentKey, callbacks, controller.signal);
      } else {
        setLoader(true);
        sendChatBotMessages(chatInput, undefined, {
          onChunk: () => {},
          onDone: () => setTrigger(Date.now()),
        });
      }
    },
    [
      searchValue,
      chatMessage,
      selectedAgent,
      isStreamingRef,
      isReconnectingRef,
      chatHistory,
      isBudu,
      agentKey,
      agentKeyLoading,
      sessionId,
      uploadedFile,
      searchGlobally,
      currentUser,
      chatUserId,
      setIsStreamingWithRef,
      setSessionStatus,
      setGenerationId,
      abortRef,
      sendChatBotMessages,
      refreshHistory,
    ],
  );

  const handleStartEditMessage = useCallback(
    (index: number) => {
      if (isStreamingRef.current || isReconnectingRef.current) return;
      setEditingIndex(index);
      setEditDraft(chatHistory[index]?.content ?? "");
    },
    [chatHistory, isStreamingRef, isReconnectingRef],
  );

  const handleCancelEditMessage = useCallback(() => {
    setEditingIndex(null);
    setEditDraft("");
  }, []);

  const handleSubmitEditMessage = useCallback(
    (index: number) => {
      if (!editDraft.trim()) return;
      const draft = editDraft;
      setEditingIndex(null);
      setEditDraft("");
      handleButtonClick(draft, undefined, index);
    },
    [editDraft, handleButtonClick],
  );

  const handleResumeStream = useCallback(() => {
    if (isStreamingRef.current || isReconnectingRef.current) return;
    if (generationId && sessionId) {
      reconnectStream(generationId, sessionId);
    }
  }, [
    generationId,
    sessionId,
    reconnectStream,
    isStreamingRef,
    isReconnectingRef,
  ]);

  const handleCloseModal = async () => {
    resetStream();
    setLoader(false);
    setIsModalOpen(false);
    setChatHistory([]);
    setFeedbackGiven({});
    setSessionId(null);
    setChatUserId(null);
    setUploadedFile(null);
    setUploadError(null);
    setSearchGlobally(false);
    setEditingIndex(null);
    setEditDraft("");
    await updateAIChatSessions();
    await refreshHistory(limit.five);
  };

  const fetchSession = async (sessionId: number): Promise<ISessionDetail> => {
    setIsFetchingSession(true);
    try {
      return await loadSessionMessages(sessionId);
    } finally {
      setIsFetchingSession(false);
    }
  };

  const handleHistoryItemClick = useCallback(
    async (item: IHistoryItem) => {
      setShowSuggestions(false);
      setIsModalOpen(true);
      resetStream();
      setChatHistory([]);
      setFeedbackGiven({});
      setEditingIndex(null);
      setEditDraft("");
      setSessionId(item.id);
      setLoader(true);

      let delegatedToSend = false;
      try {
        const sessionDetail = await fetchSession(item.id);
        const messages = sessionDetail.messages || [];
        if (messages.length > 0) {
          setChatHistory(messages);

          if (
            sessionDetail.status === "Pending" &&
            sessionDetail.generationId
          ) {
            setGenerationId(sessionDetail.generationId);
            setSessionStatus("Pending");
            reconnectStream(sessionDetail.generationId, item.id);
          }

          if (sessionDetail.uploadId && sessionDetail.uploadFilename) {
            setUploadedFile({
              uploadId: sessionDetail.uploadId,
              filename: sessionDetail.uploadFilename,
            });
          } else {
            setUploadedFile(null);
          }
        } else if (item.title) {
          delegatedToSend = true;
          handleButtonClick(item.title, item.id);
        }
      } catch (err) {
        console.error("Failed to load session:", err);
        setChatHistory([
          {
            role: AiChatRoles.ASSISTANT,
            content: "Failed to load this conversation. Please try again.",
          },
        ]);
      } finally {
        if (!delegatedToSend && !isReconnectingRef.current) setLoader(false);
      }
    },
    [
      resetStream,
      handleButtonClick,
      loadSessionMessages,
      reconnectStream,
      setGenerationId,
      setSessionStatus,
      isReconnectingRef,
    ],
  );

  const handleMaximizeClick = () => {
    setIsFullScreen((prev) => !prev);
  };

  const handleSuggestionClick = useCallback(
    (item: IHistoryItem) => {
      handleHistoryItemClick(item);
    },
    [handleHistoryItemClick],
  );

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleFeedback = useCallback(
    async (messageIndex: number, rating: number, correctionText?: string) => {
      if (!sessionId) return;
      if (!correctionText) {
        setFeedbackGiven((prev) => ({ ...prev, [messageIndex]: rating }));
      }
      const userQuestion = chatHistory[messageIndex - 1]?.content;
      const chatResponse = chatHistory[messageIndex]?.content;
      try {
        await submitFeedbackAction(
          sessionId,
          messageIndex,
          rating,
          userQuestion,
          chatResponse,
          correctionText,
        );
      } catch {
        // UI already updated optimistically; ignore API failure silently
      }
    },
    [sessionId, chatHistory, submitFeedbackAction],
  );

  const handleFileSelect = useCallback(
    async (file: File) => {
      setIsUploadingFile(true);
      setUploadError(null);
      try {
        const result = await uploadFileAction(file, sessionId, "AgentChat");
        setUploadedFile(result);
      } catch (err: any) {
        console.error("File upload failed:", err);
        setUploadError("error");
      } finally {
        setIsUploadingFile(false);
      }
    },
    [uploadFileAction, sessionId],
  );

  const handleFileRemove = useCallback(async () => {
    setUploadError(null);
    if (!uploadedFile) return;
    try {
      await removeFileAction(uploadedFile.uploadId);
    } catch {
      // ignore
    }
    setUploadedFile(null);
    setSearchGlobally(false);
  }, [uploadedFile, removeFileAction]);

  const sharedChatProps = {
    isModalOpen,
    isFullScreen,
    chatHistory,
    loader,
    isBusy,
    isStreaming,
    chatMessage,
    copied,
    onClose: handleCloseModal,
    onMaximize: handleMaximizeClick,
    onChatMessageChange: setChatMessage,
    onSend: handleButtonClick,
    onStop: handleStopGeneration,
    onCopy: handleCopy,
  };

  return (
    <>
      <div className="search">
        <div
          className="position-relative"
          onMouseEnter={() => setShowSuggestions(true)}
          onMouseLeave={() => setShowSuggestions(false)}
        >
          <input
            type="text"
            name="search"
            placeholder="How can I help ?"
            value={searchValue}
            onChange={handleInputChange}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleButtonClick();
              }
            }}
          />
          {showSuggestions && (
            <ul className="bot-suggesition">
              {previousChatMessage?.map((suggestion, index) => (
                <li
                  key={index}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    borderBottom:
                      index < previousChatMessage?.length - 1
                        ? "1px solid #eee"
                        : "none",
                  }}
                  onClick={() => handleSuggestionClick(suggestion)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f0f0f0";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                >
                  {suggestion.title}
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          onClick={() => handleButtonClick()}
          disabled={isBusy}
          style={{
            top: 0,
            right: 0,
            height: "44px",
            opacity: isBusy ? 0.5 : 1,
            cursor: isBusy ? "not-allowed" : "pointer",
          }}
        >
          <img src="/assets/images/icon/ai.svg" alt="" />
        </button>
      </div>

      {isBudu ? (
        <BuduGPTChat
          {...sharedChatProps}
          isFetchingSession={isFetchingSession}
          feedbackGiven={feedbackGiven}
          uploadedFile={uploadedFile}
          isUploadingFile={isUploadingFile}
          uploadError={uploadError}
          searchGlobally={searchGlobally}
          onFeedback={handleFeedback}
          onFileSelect={handleFileSelect}
          onFileRemove={handleFileRemove}
          onToggleSearch={() => setSearchGlobally((prev) => !prev)}
          sessionStatus={sessionStatus}
          onResume={
            generationId && sessionStatus === "Pending" && !isBusy
              ? handleResumeStream
              : undefined
          }
          editingIndex={editingIndex}
          editDraft={editDraft}
          onEditDraftChange={setEditDraft}
          onStartEdit={handleStartEditMessage}
          onCancelEdit={handleCancelEditMessage}
          onSubmitEdit={handleSubmitEditMessage}
        />
      ) : (
        <CopilotChat {...sharedChatProps} />
      )}

      <a id="prime" className="fab">
        <i className="prime zmdi zmdi-comment-outline"></i>
      </a>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  message: state.chatbot.message,
  response: state.chatbot.response,
  previousChatMessage: state.chatbot?.aiPreviousChatMessage ?? [],
  agentKey: state.aiConfiguration.aiAgentKey,
  agentKeyLoading: state.aiConfiguration.aiAgentKeyLoading,
  currentUser: state.userDetails.userProfileData,
});

const mapDispatchToProps = {
  sendChatBotMessages: (
    data: IMessage,
    agentKey?: string,
    callbacks?: ISSECallbacks,
    signal?: AbortSignal,
  ) => aiChatAgent(data, agentKey, callbacks, signal),
  updateAIChatSessions: () => updateAISessions(),
  refreshHistory: (limit: number) => getPreviousChatMessage(limit),
  loadSessionMessages: (sessionId: number) =>
    loadChatSessionMessages(sessionId),
  submitFeedbackAction: (
    sessionId: number,
    messageIndex: number,
    rating: number,
    userQuestion?: string,
    chatResponse?: string,
    correctionText?: string,
  ) =>
    submitFeedback(
      sessionId,
      messageIndex,
      rating,
      userQuestion,
      chatResponse,
      correctionText,
    ),
  uploadFileAction: (file: File, sessionId?: number | null, sourceType?: string) => uploadFile(file, sessionId, sourceType),
  removeFileAction: (uploadId: string) => removeFile(uploadId),
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchWithAI);
